
import React from 'react';
import { LanguageContext } from '../../utilities/LocalizationModule';
import { Panel, Label, Stack, Dropdown, PrimaryButton, Toggle, IDropdownOption, PanelType } from "@fluentui/react";
import { ITenantUser } from '../../data-structures/interfaces';
import { labelHeadingStyle, dropdownStyles, labelInfoStyle } from "../../styles/PanelStyle";
import { connect, ConnectedProps } from 'react-redux';
import { RootState } from '../../redux';
import { updateUser } from '../../redux/modules/user';
import { LandingPage, LicenseType, SubscriptionStatusEnum, SubscriptionTierEnum } from '../../data-structures/enums';
import { getTenantSubscriptions } from '../../utilities/helpers/ApiHelper';

const mapStateToProps = (state: RootState) => {
    return {
        isPartner: state.user.isPartner,
        isGlobalAdmin: state.user.isGlobalAdmin,
        isTenant: state.user.isTenantAdmin,
        tenants: state.user.tenants
    };
};

const mapDispatchToProps = {
    updateUser
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface EditPanelProps extends PropsFromRedux {
    isOpen: boolean,
    closePanel: () => void,
    tenantUser: ITenantUser,
};

const EditPanel: React.FC<EditPanelProps> = ({
    isOpen,
    closePanel,
    tenantUser,
    isPartner,
    isGlobalAdmin,
    updateUser,
    tenants
}: EditPanelProps) => {
    const languageStrings = React.useContext(LanguageContext);

    const [newTenantUser, setNewTenantUser] = React.useState<ITenantUser>({ ...tenantUser })
    const [originalTenantUser, setOriginalTenantUser] = React.useState<ITenantUser>({ ...tenantUser });
    const [isSaveDisabled, setIsSaveDisabled] = React.useState<boolean>(true);
    const [subscriptions, setSubscriptions] = React.useState<any[]>([]);
    const [subscriptionOptions, setSubscriptionOptions] = React.useState<IDropdownOption[]>([]);

    React.useEffect(() => {
        if (isOpen) {
            (async () => {
                if (tenantUser && tenants?.length > 0) {
                    const tenant = tenants.find(x => x.tenantId === tenantUser.tenantId);
                    const subscriptions = await getTenantSubscriptions(tenant.id, languageStrings);

                    setSubscriptions(subscriptions);

                    const tenantSubs: IDropdownOption[] = subscriptions
                        .filter(x => {

                            if (newTenantUser?.subscriptionId && x.id === newTenantUser.subscriptionId) {
                                return true;
                            }

                            const status: string = (x.status as string)?.toLowerCase();

                            if (
                                status === SubscriptionStatusEnum.Completed
                                || status === SubscriptionStatusEnum.Suspended
                                || (status === SubscriptionStatusEnum.Canceled && (!x.expiryDateRaw || x.expiryDateRaw < new Date()))
                                || (x.expiryDateRaw && x.expiryDateRaw < new Date())
                            ) {
                                return false;
                            }

                            return true;
                        })
                        .sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime())
                        .map(sub => {
                            let licenseText: string;
                            const status: string = (sub.status as string)?.toLowerCase();

                            // if (status === SubscriptionStatusEnum.Canceled) {
                            //     licenseText = `(${languageStrings.Canceled})`;
                            // } else if (sub.method === "invoice" || sub.method === "invoice_static" || sub.method === "invoice_dynamic") {
                            //     licenseText = `(${languageStrings.Invoice})`;
                            // } else if (sub.tier === SubscriptionTierEnum.Trial) {
                            //     licenseText = `(${new Date(sub.expiryDate).toLocaleDateString()})`;
                            // } else {
                            //     licenseText = `(${sub.availableLicenses} ${languageStrings.Available.toLocaleLowerCase()})`;
                            // }

                            if (
                                status === SubscriptionStatusEnum.Suspended
                                || status === SubscriptionStatusEnum.Completed
                                || (status === SubscriptionStatusEnum.Canceled && (!sub.expiryDateRaw || sub.expiryDateRaw < new Date()))
                            ) {
                                licenseText = `(${languageStrings.Inactive})`;
                            } else {
                                licenseText = `(${sub.availableLicenses !== "-" ? sub.availableLicenses : languageStrings.Unlimited} ${languageStrings.Available.toLocaleLowerCase()})`;
                            }

                            if (sub.interval) {
                                licenseText += sub.interval === languageStrings.OnceOff
                                    ? "- Trial"
                                    : ` - ${sub.interval}`;
                            }

                            const option: IDropdownOption = {
                                key: sub.id,
                                text: `${sub.product} ${licenseText}`,
                                data: sub,
                                disabled: sub.availableLicenses === 0
                            }

                            return option;
                        });

                    tenantSubs.unshift({
                        key: "revokeUser",
                        text: "",
                    });

                    setSubscriptionOptions(tenantSubs);
                }
            })();
        }
    }, [isOpen, tenantUser, tenants])

    React.useEffect(() => {
        setOriginalTenantUser({ ...tenantUser })
        setNewTenantUser({ ...tenantUser })
    }, [isOpen, tenantUser])

    React.useEffect(() => {
        isDirty()
    }, [newTenantUser])

    const landingPageOptions = [
        { key: LandingPage.Default, text: languageStrings.Default },
        { key: LandingPage.SharePoint, text: "SharePoint" },
        { key: LandingPage.Teams, text: "Teams" },
        { key: LandingPage.OneDrive, text: "OneDrive" },
        { key: LandingPage.Exchange, text: "Exchange" }
    ]

    const isDirty = () => {
        let enableSave: boolean = false;
        const userKeys = Object.keys(newTenantUser);
        userKeys.every(key => {
            if (key === "key") return true //skip key check, bc we get a new key on rerender 
            else {
                if (typeof newTenantUser[key] != "object") { //if it is not an array or object, do this code
                    if (newTenantUser[key] != originalTenantUser[key]) {
                        enableSave = true;
                        return false;
                    } else {
                        return true
                    }
                } else {
                    if (key === "licenseExpiryDate" && new Date(newTenantUser[key]).toDateString() != new Date(originalTenantUser[key]).toDateString()) {
                        enableSave = true;
                        return false
                    } else {
                        return true
                    }
                }
            }
        })
        setIsSaveDisabled(!enableSave);
    }

    const isActivatedEnabled = () => {
        if (originalTenantUser.isActivated === false) {
            const userSubscription = subscriptions.find(sub => sub.id === newTenantUser.subscriptionId);
            if (userSubscription) {
                if (userSubscription.status?.toLowerCase() !== SubscriptionStatusEnum.Active
                    || (userSubscription.expiryDate && userSubscription.expiryDate !== "-" && userSubscription.tier === SubscriptionTierEnum.Trial && new Date(userSubscription.expiryDate) < new Date())
                    || (!(userSubscription.method === "invoice" || userSubscription.method === "invoice_static" || userSubscription.method === "invoice_dynamic") && userSubscription.availableLicenses <= 0)) {
                    return false;
                }
            }
        }
        return true;
    }

    return (
        <Panel
            headerText="Edit user"
            isOpen={isOpen}
            isLightDismiss
            onDismiss={closePanel}
            closeButtonAriaLabel="Close"
            type={PanelType.custom}
            customWidth={"500px"}
        >
            <Stack tokens={{ childrenGap: 15 }}>
                <Stack.Item style={{ marginTop: 15 }}>
                    <Label styles={labelHeadingStyle}>{languageStrings.Username}</Label>
                    <Label styles={labelInfoStyle} disabled>{tenantUser?.username}</Label>
                </Stack.Item>
                <Stack.Item>
                    <Label styles={labelHeadingStyle}>{languageStrings.DisplayName}</Label>
                    <Label styles={labelInfoStyle} disabled>{tenantUser?.displayName}</Label>
                </Stack.Item>
                <Stack.Item>
                    <Label styles={labelHeadingStyle}>{languageStrings.Subscription}</Label>
                    {subscriptionOptions?.length > 0 && <Dropdown
                        placeholder={languageStrings.SelectSubscription}
                        options={subscriptionOptions}
                        onChange={(_e, o) => setNewTenantUser({ ...newTenantUser, subscriptionId: Number(o.key) })}
                        selectedKey={newTenantUser?.subscriptionId ? newTenantUser?.subscriptionId?.valueOf() : subscriptionOptions[0].key}
                    />}
                </Stack.Item>
                <Stack.Item>
                    <Toggle
                        label={<Label styles={labelHeadingStyle}>{languageStrings.Active}</Label>}
                        checked={newTenantUser.isActivated}
                        onChange={(_event: React.MouseEvent<HTMLElement, MouseEvent>, checked?: boolean) => setNewTenantUser({ ...newTenantUser, isActivated: checked ?? false })}
                        disabled={!isActivatedEnabled()}
                        onText={languageStrings.Yes}
                        offText={languageStrings.No}
                    />
                </Stack.Item>
                <Stack.Item>
                    <Toggle
                        label={<Label styles={labelHeadingStyle}>{languageStrings.TenantAdmin}</Label>}
                        checked={newTenantUser.isTenantAdmin}
                        onChange={(_event: React.MouseEvent<HTMLElement, MouseEvent>, checked?: boolean) => setNewTenantUser({ ...newTenantUser, isTenantAdmin: checked ?? false })}
                        disabled={!(isPartner || isGlobalAdmin)}
                        onText={languageStrings.Yes}
                        offText={languageStrings.No}
                    />
                </Stack.Item>
                <Stack.Item>
                    <Toggle
                        label={<Label styles={labelHeadingStyle}>{languageStrings.Partner}</Label>}
                        checked={newTenantUser.isPartner}
                        onChange={(_event: React.MouseEvent<HTMLElement, MouseEvent>, checked?: boolean) => setNewTenantUser({ ...newTenantUser, isPartner: checked ?? false })}
                        disabled={true}
                        onText={languageStrings.Yes}
                        offText={languageStrings.No}
                    />
                </Stack.Item>
                <Stack.Item >
                    <Label styles={labelHeadingStyle}>{languageStrings.GeneralSettings}</Label>
                    <Toggle
                        label={languageStrings.Show + " SharePoint"}
                        checked={newTenantUser.enableSharePoint}
                        onChange={(_event: React.MouseEvent<HTMLElement, MouseEvent>, checked?: boolean) => setNewTenantUser({ ...newTenantUser, enableSharePoint: checked ?? false })}
                        onText={languageStrings.Yes}
                        offText={languageStrings.No}
                    />
                    <Toggle
                        label={languageStrings.Show + " Teams"}
                        checked={newTenantUser.enableTeams}
                        onChange={(_event: React.MouseEvent<HTMLElement, MouseEvent>, checked?: boolean) => setNewTenantUser({ ...newTenantUser, enableTeams: checked ?? false })}
                        onText={languageStrings.Yes}
                        offText={languageStrings.No}
                    />
                    <Toggle
                        label={languageStrings.Show + " OneDrive"}
                        checked={newTenantUser.enableOneDrive}
                        onChange={(_event: React.MouseEvent<HTMLElement, MouseEvent>, checked?: boolean) => setNewTenantUser({ ...newTenantUser, enableOneDrive: checked ?? false })}
                        onText={languageStrings.Yes}
                        offText={languageStrings.No}
                    />
                    <Toggle
                        label={languageStrings.EnableApprovals}
                        checked={newTenantUser.enableApprovals}
                        onChange={(_event: React.MouseEvent<HTMLElement, MouseEvent>, checked?: boolean) => setNewTenantUser({ ...newTenantUser, enableApprovals: checked ?? false })}
                        onText={languageStrings.Yes}
                        offText={languageStrings.No}
                    />
                    <Stack.Item>
                        <Label styles={labelHeadingStyle}>{languageStrings.LandingPage}</Label>
                        <Dropdown
                            style={{ marginTop: 7 }}
                            styles={dropdownStyles}
                            selectedKey={newTenantUser?.landingPage ? newTenantUser?.landingPage?.valueOf() : landingPageOptions[0].key}
                            onChange={(_e, o) => setNewTenantUser({ ...newTenantUser, landingPage: Number(o.key) })}
                            options={landingPageOptions}
                        />
                    </Stack.Item>
                </Stack.Item>

                <Stack.Item>
                    <Label styles={labelHeadingStyle}>
                        {languageStrings.OutlookSettings}
                    </Label>
                    <Toggle
                        disabled={true}
                        label={languageStrings.SaveOnSend}
                        checked={newTenantUser.saveOnSend ? true : false}
                        onChange={(_event: React.MouseEvent<HTMLElement, MouseEvent>, checked?: boolean) => setNewTenantUser({ ...newTenantUser, saveOnSend: checked ?? false })}
                        onText={languageStrings.Yes}
                        offText={languageStrings.No}
                    />
                    <Toggle
                        label={languageStrings.AutoSelectAttachments}
                        checked={newTenantUser.autoSelectAttachments ? true : false}
                        onChange={(_event: React.MouseEvent<HTMLElement, MouseEvent>, checked?: boolean) => setNewTenantUser({ ...newTenantUser, autoSelectAttachments: checked ?? false })}
                        onText={languageStrings.Yes}
                        offText={languageStrings.No}
                    />
                    <Toggle
                        label={languageStrings.AutoRemoveMailItemAttachments}
                        checked={newTenantUser.autoRemoveMailItemAttachments ? true : false}
                        onChange={(_event: React.MouseEvent<HTMLElement, MouseEvent>, checked?: boolean) => setNewTenantUser({ ...newTenantUser, autoRemoveMailItemAttachments: checked ?? false })}
                        onText={languageStrings.Yes}
                        offText={languageStrings.No}
                    />
                    <Toggle
                        label={languageStrings.ConvertEmailToPdf}
                        checked={newTenantUser.convertEmailToPDF ? true : false}
                        onChange={(_event: React.MouseEvent<HTMLElement, MouseEvent>, checked?: boolean) => setNewTenantUser({ ...newTenantUser, convertEmailToPDF: checked ?? false })}
                        onText={languageStrings.Yes}
                        offText={languageStrings.No}
                    />
                </Stack.Item>
                <Stack horizontal horizontalAlign="end">
                    <PrimaryButton
                        disabled={isSaveDisabled}
                        iconProps={{ iconName: 'save' }}
                        onClick={() => {
                            updateUser(newTenantUser);
                            closePanel();
                        }}>
                        {languageStrings.Save}
                    </PrimaryButton>
                </Stack>

            </Stack>

        </Panel>
    )
}

export default connector(EditPanel);